/**
 * O período que um relatório pode pedir.
 *
 * Isto existe porque o período vira `WHERE data BETWEEN ... AND ...` numa query
 * do Snowflake. Um intervalo aberto demais não erra: ele responde — lento, caro
 * e cobrando warehouse. Um `desde` com ano digitado errado (`0226`) varre a
 * tabela inteira. O teto e o piso daqui são o que impede a tela de mandar isso.
 *
 * Tudo aqui é função pura com o "hoje" injetado. Sem isso não dá para testar a
 * borda que mais importa — a virada do dia — e o `jest.config.cjs` roda com
 * `testEnvironment: node`, então não há como renderizar o campo para testar.
 */

/**
 * Teto do intervalo, em dias. Um ano cheio (com bissexto) cobre o caso real de
 * fechamento anual; acima disso é quase sempre engano de digitação, e o custo
 * cai no warehouse do cliente.
 */
export const MAX_RANGE_DAYS = 366;

/**
 * Piso absoluto. Não existe movimentação anterior à operação da plataforma, e
 * um ano digitado errado no campo de data (`0226-08-01`, `1026-08-01`) passa
 * pelo formato ISO sem tropeçar — é este piso que o pega.
 */
export const MIN_REPORT_DATE = "2020-01-01";

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

/**
 * O dia de hoje no fuso de quem está olhando a tela.
 *
 * `toISOString()` não serve: ele devolve UTC, e no Brasil (UTC-3) qualquer
 * horário depois das 21h vira o dia seguinte — o usuário seria proibido de
 * pedir o próprio dia de hoje, ou autorizado a pedir amanhã.
 */
export function todayIsoLocal(now: Date = new Date()): string {
  const year = String(now.getFullYear()).padStart(4, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * `null` quando a string não é uma data de calendário real.
 *
 * O formato sozinho não basta: `2026-02-30` e `2026-13-01` passam por
 * `/^\d{4}-\d{2}-\d{2}$/` e o `new Date` os transborda em silêncio para
 * 02/03 e 01/01/2027. O round-trip pelos componentes é o que denuncia.
 *
 * UTC de propósito — o valor é uma data sem hora, e o horário de verão faria
 * dias de 23h ou 25h aparecerem na subtração.
 */
function toUtcTimestamp(value: string): number | null {
  if (!ISO_DATE_PATTERN.test(value)) return null;

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));

  // `Date.UTC(1, 0, 1)` devolve 1901, não ano 1: a assinatura legada remapeia
  // ano de 0 a 99 para 1900+. Sem o `setUTCFullYear`, um ano digitado com um
  // dígito só reprovaria como "Data inválida" em vez de bater no piso, que é a
  // mensagem que diz o que houve.
  const parsed = new Date(0);
  parsed.setUTCFullYear(year, month - 1, day);
  parsed.setUTCHours(0, 0, 0, 0);
  const timestamp = parsed.getTime();
  if (Number.isNaN(timestamp)) return null;
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return timestamp;
}

/** Dias entre duas datas ISO já validadas. Inclusivo dos dois lados. */
export function rangeLengthInDays(desde: string, ate: string): number {
  const from = toUtcTimestamp(desde);
  const to = toUtcTimestamp(ate);
  if (from === null || to === null) return 0;
  return Math.round((to - from) / MS_PER_DAY) + 1;
}

/**
 * `null` = período aceitável. A ordem das checagens é a ordem em que a pessoa
 * consegue corrigir: primeiro o campo que está malformado, depois a relação
 * entre os dois, depois o tamanho.
 */
export function validateDateRange(
  desde: string,
  ate: string,
  today: string = todayIsoLocal()
): string | null {
  if (!desde || !ate) return "Informe a data inicial e a final";

  const from = toUtcTimestamp(desde);
  const to = toUtcTimestamp(ate);
  if (from === null || to === null) return "Data inválida";

  const floor = toUtcTimestamp(MIN_REPORT_DATE)!;
  if (from < floor || to < floor) {
    return `Não há dado anterior a ${formatBr(MIN_REPORT_DATE)}`;
  }

  // O "hoje" vem de fora e pode ser lixo em teoria; sem data confiável, não dá
  // para afirmar que algo está no futuro — melhor deixar passar e o backend
  // decidir do que recusar um período legítimo.
  const limit = toUtcTimestamp(today);
  if (limit !== null && (from > limit || to > limit)) {
    return "O período não pode passar de hoje";
  }

  if (from > to) return "A data inicial não pode ser depois da final";

  const length = Math.round((to - from) / MS_PER_DAY) + 1;
  if (length > MAX_RANGE_DAYS) {
    return `O período não pode passar de ${MAX_RANGE_DAYS} dias (você pediu ${length})`;
  }

  return null;
}

/** `2026-08-12` → `12/08/2026`, só para a mensagem de erro. */
function formatBr(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}
